import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { filter, take, tap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { EditAccessPointComponent } from 'src/app/components/shared/modal/edit-access-point/edit-access-point.component';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LocationProfile,
  RouteAdmin,
  UserAdmin,
} from 'src/app/core/models/admin';
import {
  AdlDetail,
  AdlDetails,
  GetAllAdls,
} from 'src/app/core/models/admin/adl.model';
import { RouteSummary } from 'src/app/core/models/operations';
import { AdlService } from 'src/app/core/services/admin/adl/adl.service';
import { RouteService } from 'src/app/core/services/admin/route/route.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  ColumnIds,
  ColumnText,
  DISPLAY_TEXT,
  EXCEL_FILE_NAME,
} from './route-admin-non-ido.constants';

@Component({
  selector: 'app-route-admin-non-ido',
  templateUrl: './route-admin-non-ido.component.html',
  styleUrls: ['./route-admin-non-ido.component.scss'],
  standalone: false,
})
export class RouteAdminNonIdoComponent implements OnChanges {
  @Input() currentLocation!: LocationProfile;

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);
  private adlService = inject(AdlService);

  //Display Constants
  readonly CONSTANTS = DISPLAY_TEXT;
  readonly IDS = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly COLUMN_IDS = ColumnIds;

  dataSource = new MatTableDataSource<AdlDetails>();
  originalTableData!: AdlDetails[];
  allAdls!: AdlDetails[];

  // Column Search Variables
  routeData = this.commonUtils.createTableColumn(ColumnIds.ROUTE);
  adlData = this.commonUtils.createTableColumn(ColumnIds.ADL);
  routeDetailsDate = new Date();
  constructor(
    private dialog: MatDialog,
    private readonly commonUtils: CommonUtils,
    private routeService: RouteService,
  ) {}

  @ViewChild(MatSort) sort!: MatSort;

  ngOnChanges(changes: SimpleChanges): void {
    this.fetchAllData(changes.currentLocation.currentValue.locationCode);
  }

  exportExcel() {
    const excelData: string[][] = [];
    this.dataSource.filteredData.forEach((value) => {
      value.adls.forEach((adl: AdlDetail) => {
        excelData.push([value.loop, adl?.name]);
      });
    });

    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      excelData,
    );
  }

  clearAllFilters(): void {
    this.commonUtils.clearAllFilters(this.dataSource, this.originalTableData, [
      this.routeData.formControl,
      this.adlData.formControl,
    ]);
  }

  search(): void {
    this.commonUtils.searchFilter(
      [this.routeData, this.adlData],
      this.dataSource,
    );
  }

  openEditAdl(index: number) {
    const editAdl = this.dialog.open(EditAccessPointComponent, {
      width: '56.25rem',
      height: '49rem',
      disableClose: true,
      data: {
        data: this.dataSource.data[index]?.adls,
        name: this.dataSource.data[index]?.loop,
      },
    });
    editAdl?.afterClosed().subscribe((resp) => {
      if (resp === BUTTON_CLICK.ACCEPT) {
        //Wait for BE to update table so we can get the updated data
        setTimeout(() => {
          this.fetchAllData(this.currentLocation.locationCode);
        }, 1000);
      }
    });
  }

  addRoute($event: RouteSummary) {
    const route = {
      is_optimizeroute: '1',
      break_exist: '1',
      route_id: $event.route,
    } as RouteAdmin;
    this.routeService
      .createRoute(route, this.currentLocation?.locationCode)
      .pipe(take(1))
      .subscribe(() => {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        this.fetchAllData(this.currentLocation.locationCode);
      });
  }

  removeRoute(index: number) {
    if (this.dataSource.data[index]?.adls.length > 0) {
      this.openWarningDialog();
    } else {
      this.openConfirmationDialog(index);
    }
  }

  get isFilterEnabled() {
    return (
      this.routeData.formControl.value !== '' ||
      this.adlData.formControl.value !== ''
    );
  }

  private openConfirmationDialog(index: number) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTANTS.CONFIRM_MODAL.HEADER,
        body: this.CONSTANTS.CONFIRM_MODAL.BODY,
        extraText: this.dataSource.data[index]?.loop,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.userService
            .getCurrentUser$()
            .pipe(
              takeUntilDestroyed(this.destroy),
              filter((user): user is UserAdmin => !!user),
              tap((user) => {
                this.routeService
                  .deleteRoute(
                    this.dataSource.data[index].loop,
                    user?.location,
                    user?.loginId,
                  )
                  .pipe(take(1))
                  .subscribe(() => {
                    this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                    this.fetchAllData(this.currentLocation.locationCode);
                  });
              }),
            )
            .subscribe();
        }
      });
  }

  private openWarningDialog() {
    this.commonUtils.openWarningModal({
      header: this.CONSTANTS.MODAL.HEADER,
      body: this.CONSTANTS.MODAL.BODY,
      confirmButtonText: this.CONSTANTS.MODAL.CONFIRM_BUTTON,
      rejectButtonText: '',
    });
  }

  private fetchAllData(locationCode: string) {
    this.adlService
      .getAllAdlsByLocation(locationCode)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((data: GetAllAdls) => {
        let adlOverview = data?.response?.adlOverview;
        adlOverview?.sort((a, b) => a?.loop?.localeCompare(b?.loop));
        adlOverview = adlOverview.map((val) => {
          const newAdls = this.commonUtils.filterOutInvalidAccessPoints(
            val?.adls || [],
          ) as AdlDetail[];
          return { loop: val.loop, adls: newAdls };
        });
        this.originalTableData = adlOverview;
        this.dataSource.data = adlOverview;
        this.dataSource.filterPredicate = this.commonUtils.createFilter();
      });
  }
}
